/**
 * BasicInfoStep.js  — Assessment Wizard, Screen 2 "Basic Info"
 *
 * Collects core biometric data required for BMI/BMR calculation: Name, Age,
 * Gender, Mobile number, Email, Height, Weight, and an optional Target
 * Weight. Built on the same AssessmentStepShell as GoalStep so the progress
 * bar, step counter, and Previous/Next footer stay identical across the
 * wizard.
 *
 * Rule source: FSD v2.1 "Decision Closure & Production Defaults" (DEC-08
 * Minor users, §3.1 Adult energy engine, §9 Minor User Policy) + AI
 * Nutrition Policy & Rule Engine v1.0 (§3 Adult Eligibility & Calculation
 * Baseline). Supersedes the FSD v1.0 rules noted inline below.
 *
 * Validation rules (current production baseline):
 *  - Name: optional, max 50 chars, letters/spaces only.
 *  - Age: required, integer, 13–90 accepted as *input*, but the
 *    personalized calorie-planning engine is restricted to adults ≥18
 *    (DEC-08 / §9 Minor User Policy — do not silently convert a minor to
 *    adult calculations):
 *      • age < 13  → hard-blocks progression, pediatric-consult message.
 *      • age 13–17 → also blocks progression (adult BMI/BMR engine does
 *        not apply; the product does not yet have a separate pediatric
 *        engine), distinct "planner is 18+" message.
 *      • age 18–90 → proceeds normally into the adult engine.
 *      • age > 90 or non-integer → normal inline error.
 *    (FSD v1.0 previously allowed 13–90 straight into the adult engine —
 *    that behavior is no longer correct and is fixed here.)
 *  - Gender: required, single-select. Selecting Female is surfaced to the
 *    parent via onGenderChange so it can unlock Screen 11 later; the value
 *    itself is also included in the onNext payload either way.
 *  - Mobile: required by default (flip MOBILE_REQUIRED to make it
 *    optional). Country code is picked via CountryCodeSelect.js — a
 *    searchable combobox over every country in countryCodes.js — defaulting
 *    to DEFAULT_COUNTRY_CODE. Digit-length rules are per-country; see
 *    countryCodes.js's header for what's country-verified vs. generic. The
 *    canonical value handed to onNext is E.164 (`mobileE164`, e.g.
 *    "+919876543210") — the dial code and the national number are also
 *    passed separately so callers don't have to re-split.
 *  - Email: optional, standard format check only (not a deliverability
 *    check — no MX lookup, no confirmation send from this screen).
 *  - Height: required, 100–250 cm (or ft/in toggle, converted for validation).
 *  - Weight: required, 30–300 kg (or lb toggle, converted for validation).
 *    (FSD v1.0 previously allowed a 25 kg floor; §3.1 Adult energy engine
 *    sets the production floor at 30 kg.)
 *  - Target Weight: optional, positive number. The "target > current weight
 *    when goal is later Weight Loss" check can't happen here (goal isn't
 *    picked until Screen 3) — GoalStep re-checks it using the weightKg/
 *    targetWeightKg this screen hands off via onNext.
 *  - Units: whatever the user toggles between (cm/ft·in, kg/lb), the
 *    canonical values handed to validation, onNext, and the calculation
 *    engine are always normalized to metric (kg, cm) per the Nutrition
 *    Policy's Units baseline — no imperial values ever leave this screen.
 *
 * Privacy (Nutrition Policy §19 Privacy & Data Minimization): raw
 * biometric answers (age, height, weight, target weight) are never sent to
 * analytics — only non-sensitive, aggregate-safe metadata is. The mobile
 * number is direct personal contact data and falls under the same rule:
 * the number itself never reaches an analytics event. Only `hasMobile`
 * (boolean) and `mobileCountry` (ISO country code) do. See
 * useBasicInfoAnalytics.js.
 */

import React, { useState, useCallback, useMemo } from "react";
import AssessmentStepShell from "../Assesment/AssessmentStepShell";
import useBasicInfoAnalytics from "./useBasicInfoAnalytics";
import { TOTAL_STEPS, STEP_NUMBERS } from "../Assesment/assessmentConfig";
import { COUNTRY_CODES } from "./countryCodes";
import CountryCodeSelect from "./CountryCodeSelect";
import {
  FormGrid,
  FieldGroup,
  Label,
  Input,
  PhoneRow,
  InlineError,
  HelperText,
  PillRow,
  Pill,
  UnitToggle,
  UnitButton,
  FieldHeaderRow,
  SplitInputRow,
  SplitInputWrap,
  BlockingNotice,
  SectionLabel,
} from "./BasicInfoStep.styles";

const STEP_NUMBER = STEP_NUMBERS.BASIC_INFO;

// ─── Conversion helpers ──────────────────────────────────────────────────────
const CM_PER_INCH = 2.54;
const KG_PER_LB = 0.45359237;

const ftInToCm = (feet, inches) => {
  const f = parseFloat(feet);
  const i = parseFloat(inches);
  if (Number.isNaN(f) && Number.isNaN(i)) return null;
  return ((Number.isNaN(f) ? 0 : f) * 12 + (Number.isNaN(i) ? 0 : i)) * CM_PER_INCH;
};

const cmToFtIn = (cm) => {
  if (typeof cm !== "number" || Number.isNaN(cm)) return { feet: "", inches: "" };
  const totalInches = cm / CM_PER_INCH;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches - feet * 12);
  return { feet: String(feet), inches: String(inches) };
};

const lbToKg = (lb) => {
  const v = parseFloat(lb);
  return Number.isNaN(v) ? null : v * KG_PER_LB;
};

const kgToLb = (kg) => {
  if (typeof kg !== "number" || Number.isNaN(kg)) return "";
  return String(Math.round((kg / KG_PER_LB) * 10) / 10);
};

// ─── Rule constants (FSD v2.1 §3.1 / §9; Nutrition Policy v1.0 §3) ──────────
// Recorded here (not just in comments) so they're easy to audit against the
// rule registry / versioned config the Nutrition Expert signs off on.
const RULE_VERSION = "FSD v2.1 / Nutrition Policy v1.0";
const PEDIATRIC_MIN_AGE = 13; // below this: hard block, pediatric-consult message
const ADULT_MIN_AGE = 18; // 13-17: blocked from the adult planning engine (DEC-08)
const MAX_AGE = 90;
const WEIGHT_MIN_KG = 30; // §3.1 Adult energy engine input validation
const WEIGHT_MAX_KG = 300;
const HEIGHT_MIN_CM = 100;
const HEIGHT_MAX_CM = 250;

// ─── Mobile number config ────────────────────────────────────────────────────
// Set to false to collect the number without blocking progression on it.
const MOBILE_REQUIRED = true;
const DEFAULT_COUNTRY_CODE = "IN";
const MAX_NATIONAL_DIGITS = Math.max(...COUNTRY_CODES.map((c) => c.max));

const findCountry = (code) =>
  COUNTRY_CODES.find((c) => c.code === code) ??
  COUNTRY_CODES.find((c) => c.code === DEFAULT_COUNTRY_CODE) ??
  COUNTRY_CODES[0];

/**
 * Keep only digits, drop a leading 0 (common local-dialling habit — "098765…"
 * is the same number as "98765…"), and cap the length so paste-bombing a
 * huge string can't happen.
 */
const sanitizeMobile = (raw) =>
  String(raw ?? "")
    .replace(/\D/g, "")
    .replace(/^0+/, "")
    .slice(0, MAX_NATIONAL_DIGITS);

// ─── Field-level validators ──────────────────────────────────────────────────
const NAME_PATTERN = /^[A-Za-z\s]{0,50}$/;

const validateName = (name) => {
  if (!name) return null; // optional
  if (name.length > 50) return "Name must be 50 characters or fewer.";
  if (!NAME_PATTERN.test(name)) return "Name can only contain letters and spaces.";
  return null;
};

/**
 * Returns { error, blocked, minor }.
 *  - blocked=true  → age < 13, hard stop (pediatric-consult message).
 *  - minor=true    → age 13-17, hard stop (adult planning engine is 18+,
 *                    DEC-08 — distinct message, not an inline field error).
 *  - error         → normal inline validation error (empty, non-integer,
 *                    or > MAX_AGE).
 * blocked and minor are mutually exclusive and both prevent progression;
 * callers that need "can't proceed" should check `blocked || minor`.
 */
const validateAge = (ageStr) => {
  if (!ageStr.trim()) return { error: "Age is required.", blocked: false, minor: false };
  const age = Number(ageStr);
  if (!Number.isInteger(age)) {
    return { error: "Age must be a whole number.", blocked: false, minor: false };
  }
  if (age < PEDIATRIC_MIN_AGE) return { error: null, blocked: true, minor: false };
  if (age < ADULT_MIN_AGE) return { error: null, blocked: false, minor: true };
  if (age > MAX_AGE) {
    return {
      error: `Age must be between ${ADULT_MIN_AGE} and ${MAX_AGE}.`,
      blocked: false,
      minor: false,
    };
  }
  return { error: null, blocked: false, minor: false };
};

const validateGender = (gender) => (gender ? null : "Please select a gender.");

// Deliberately permissive (format, not deliverability) — same spirit as the
// mobile field: catch obvious typos, don't reject valid-but-unusual emails.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateEmail = (email) => {
  if (!email.trim()) return null; // optional
  if (!EMAIL_PATTERN.test(email.trim())) return "Enter a valid email address.";
  return null;
};

/**
 * @param {string} digits  Sanitized national number (digits only).
 * @param {object} country Entry from COUNTRY_CODES.
 */
const validateMobile = (digits, country) => {
  if (!digits) return MOBILE_REQUIRED ? "Mobile number is required." : null;
  if (digits.length < country.min || digits.length > country.max) {
    return country.min === country.max
      ? `Enter a ${country.min}-digit mobile number for ${country.label}.`
      : `Enter a ${country.min}–${country.max} digit mobile number for ${country.label}.`;
  }
  if (country.leading && !country.leading.test(digits)) {
    return country.leadingMessage ?? "That doesn't look like a valid mobile number.";
  }
  return null;
};

const validateHeightCm = (cm) => {
  if (cm === null || Number.isNaN(cm)) return "Height is required.";
  if (cm < HEIGHT_MIN_CM || cm > HEIGHT_MAX_CM) {
    return "Height must be between 100–250 cm (3'3\"–8'2\").";
  }
  return null;
};

const validateWeightKg = (kg) => {
  if (kg === null || Number.isNaN(kg)) return "Weight is required.";
  if (kg < WEIGHT_MIN_KG || kg > WEIGHT_MAX_KG) {
    return "Weight must be between 30–300 kg (66–661 lb).";
  }
  return null;
};

const validateTargetWeightKg = (kg) => {
  if (kg === null) return null; // optional
  if (Number.isNaN(kg) || kg <= 0) return "Target weight must be a positive number.";
  return null;
};

const GENDER_OPTIONS = [
  { id: "male", label: "Male" },
  { id: "female", label: "Female" },
  { id: "other", label: "Other" },
];

/**
 * @param {object}   props
 * @param {string}   props.sessionId
 * @param {object}   [props.initialValues] - Previously entered values, if
 *   the user navigated back to this step. Accepts `mobileCountryCode`
 *   (ISO, e.g. "IN") and `mobileNational` (digits only); if only a full
 *   `mobileE164` is available, split it before passing it in.
 * @param {function} props.onPrevious      - Go to Screen 1 (Welcome).
 * @param {function} props.onNext          - Go to Screen 3 (Goal). Receives
 *   { name, age, gender, mobileE164, mobileCountryCode, mobileDialCode,
 *     mobileNational, email, heightCm, weightKg, targetWeightKg,
 *     ruleVersion }.
 * @param {function} [props.onGenderChange] - Called immediately on gender
 *   selection (not just on Next) so the parent can toggle Screen 11
 *   (Women's Health) into/out of the flow as soon as it's known.
 */
const BasicInfoStep = ({
  sessionId,
  initialValues = {},
  onPrevious,
  onNext,
  onGenderChange,
}) => {
  const analytics = useBasicInfoAnalytics();

  const [name, setName] = useState(initialValues.name ?? "");
  const [ageStr, setAgeStr] = useState(
    initialValues.age != null ? String(initialValues.age) : ""
  );
  const [gender, setGender] = useState(initialValues.gender ?? null);

  const [mobileCountryCode, setMobileCountryCode] = useState(
    initialValues.mobileCountryCode ?? DEFAULT_COUNTRY_CODE
  );
  const [mobileInput, setMobileInput] = useState(
    initialValues.mobileNational != null ? sanitizeMobile(initialValues.mobileNational) : ""
  );

  const [email, setEmail] = useState(initialValues.email ?? "");

  const [heightUnit, setHeightUnit] = useState("cm");
  const [heightCmInput, setHeightCmInput] = useState(
    initialValues.heightCm != null ? String(initialValues.heightCm) : ""
  );
  const [heightFeetInput, setHeightFeetInput] = useState("");
  const [heightInchesInput, setHeightInchesInput] = useState("");

  const [weightUnit, setWeightUnit] = useState("kg");
  const [weightKgInput, setWeightKgInput] = useState(
    initialValues.weightKg != null ? String(initialValues.weightKg) : ""
  );
  const [weightLbInput, setWeightLbInput] = useState("");
  const [targetWeightInput, setTargetWeightInput] = useState(
    initialValues.targetWeightKg != null ? String(initialValues.targetWeightKg) : ""
  );

  // Only show errors after the user has interacted with (blurred) a field,
  // or after a failed Next attempt — avoids nagging while first typing.
  const [touched, setTouched] = useState({});
  const [attemptedNext, setAttemptedNext] = useState(false);

  const markTouched = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  // ── Canonical (metric) values, derived from whichever unit is active ────────
  const heightCm = useMemo(() => {
    if (heightUnit === "cm") {
      const v = parseFloat(heightCmInput);
      return Number.isNaN(v) ? null : v;
    }
    return ftInToCm(heightFeetInput, heightInchesInput);
  }, [heightUnit, heightCmInput, heightFeetInput, heightInchesInput]);

  const weightKg = useMemo(() => {
    if (weightUnit === "kg") {
      const v = parseFloat(weightKgInput);
      return Number.isNaN(v) ? null : v;
    }
    return lbToKg(weightLbInput);
  }, [weightUnit, weightKgInput, weightLbInput]);

  const targetWeightKg = useMemo(() => {
    if (!targetWeightInput.trim()) return null;
    const raw = parseFloat(targetWeightInput);
    if (Number.isNaN(raw)) return NaN;
    return weightUnit === "kg" ? raw : raw * KG_PER_LB;
  }, [targetWeightInput, weightUnit]);

  const selectedCountry = useMemo(() => findCountry(mobileCountryCode), [mobileCountryCode]);

  // Canonical phone value: E.164, e.g. "+919876543210". Null until the
  // national part is present.
  const mobileE164 = useMemo(
    () => (mobileInput ? `${selectedCountry.dial}${mobileInput}` : null),
    [selectedCountry, mobileInput]
  );

  // ── Validation ────────────────────────────────────────────────────────────
  const nameError = validateName(name);
  const ageResult = validateAge(ageStr);
  const genderError = validateGender(gender);
  const mobileError = validateMobile(mobileInput, selectedCountry);
  const emailError = validateEmail(email);
  const heightError = validateHeightCm(heightCm);
  const weightError = validateWeightKg(weightKg);
  const targetWeightError = validateTargetWeightKg(targetWeightKg);

  // Both the <13 pediatric case and the 13-17 minor case block progression —
  // the adult planning engine is restricted to ages 18-90 (DEC-08).
  const isBlocked = ageResult.blocked || ageResult.minor;
  const isValid =
    !isBlocked &&
    !nameError &&
    !ageResult.error &&
    !genderError &&
    !mobileError &&
    !emailError &&
    !heightError &&
    !weightError &&
    !targetWeightError;

  const shouldShow = (field, hasError) => hasError && (touched[field] || attemptedNext);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleGenderSelect = useCallback(
    (id) => {
      setGender(id);
      analytics.trackGenderSelect(sessionId, id);
      onGenderChange?.(id);
    },
    [analytics, sessionId, onGenderChange]
  );

  // CountryCodeSelect calls back with the country's `code` directly
  // (not a DOM event) — it's a custom combobox, not a native <select>.
  const handleMobileCountryChange = useCallback(
    (code) => {
      setMobileCountryCode(code);
      markTouched("mobile");
      // Country code only — never the number itself (Nutrition Policy §19).
      analytics.trackMobileCountryChange(sessionId, code);
    },
    [analytics, sessionId]
  );

  const handleMobileChange = useCallback((e) => {
    setMobileInput(sanitizeMobile(e.target.value));
  }, []);

  const handleHeightUnitToggle = useCallback(
    (unit) => {
      if (unit === heightUnit) return;
      if (unit === "ft") {
        const { feet, inches } = cmToFtIn(heightCm);
        setHeightFeetInput(feet);
        setHeightInchesInput(inches);
      } else {
        const cm = ftInToCm(heightFeetInput, heightInchesInput);
        setHeightCmInput(cm != null ? String(Math.round(cm)) : "");
      }
      setHeightUnit(unit);
      analytics.trackUnitToggle(sessionId, "height", unit);
    },
    [heightUnit, heightCm, heightFeetInput, heightInchesInput, analytics, sessionId]
  );

  const handleWeightUnitToggle = useCallback(
    (unit) => {
      if (unit === weightUnit) return;
      if (unit === "lb") {
        setWeightLbInput(kgToLb(weightKg));
      } else {
        const kg = lbToKg(weightLbInput);
        setWeightKgInput(kg != null ? String(Math.round(kg * 10) / 10) : "");
      }
      // Target weight is always entered in whatever the current weight unit
      // is; convert its displayed value along with the toggle too.
      if (targetWeightInput.trim()) {
        const currentTargetKg = targetWeightKg;
        if (typeof currentTargetKg === "number" && !Number.isNaN(currentTargetKg)) {
          setTargetWeightInput(
            unit === "lb" ? kgToLb(currentTargetKg) : String(Math.round(currentTargetKg * 10) / 10)
          );
        }
      }
      setWeightUnit(unit);
      analytics.trackUnitToggle(sessionId, "weight", unit);
    },
    [weightUnit, weightKg, weightLbInput, targetWeightInput, targetWeightKg, analytics, sessionId]
  );

  const handlePrevious = useCallback(() => {
    analytics.trackStepPrevious(sessionId);
    onPrevious?.();
  }, [analytics, sessionId, onPrevious]);

  const handleNext = useCallback(() => {
    setAttemptedNext(true);

    if (isBlocked) {
      // Reason only — never send the raw age value to analytics
      // (Nutrition Policy §19 Privacy & Data Minimization).
      const reason = ageResult.blocked ? "under_13" : "under_18";
      analytics.trackAgeBlocked(sessionId, reason);
      return; // hard stop — see BlockingNotice below
    }
    if (!isValid) {
      analytics.trackFieldError(sessionId, "form", "validation_failed");
      return;
    }

    const payload = {
      name: name || null,
      age: Number(ageStr),
      gender,
      // Canonical contact value is E.164; the parts are included so callers
      // (OTP provider, CRM) don't have to re-parse the dial code out.
      mobileE164,
      mobileCountryCode: selectedCountry.code,
      mobileDialCode: selectedCountry.dial,
      mobileNational: mobileInput || null,
      email: email.trim() || null,
      heightCm: Math.round(heightCm * 10) / 10,
      weightKg: Math.round(weightKg * 10) / 10,
      targetWeightKg:
        typeof targetWeightKg === "number" && !Number.isNaN(targetWeightKg)
          ? Math.round(targetWeightKg * 10) / 10
          : null,
      // Record which rule set produced this validation, for audit — per
      // Nutrition Policy §19/§21, without retaining extra raw sensitive
      // inputs beyond what's already collected.
      ruleVersion: RULE_VERSION,
    };

    // Analytics gets sanitized, non-biometric metadata only — the raw
    // payload (age/height/weight/targetWeight) goes to onNext for the
    // app/calculation engine, never to analytics events. The mobile number
    // is direct contact data and is treated the same way: presence and
    // country only, never the digits.
    analytics.trackStepNext(sessionId, {
      hasName: !!payload.name,
      hasTargetWeight: payload.targetWeightKg != null,
      hasMobile: !!payload.mobileE164,
      mobileCountry: selectedCountry.code,
      hasEmail: !!payload.email,
      heightUnit,
      weightUnit,
    });
    onNext?.(payload);
  }, [
    isBlocked,
    isValid,
    ageResult,
    analytics,
    sessionId,
    ageStr,
    name,
    gender,
    mobileE164,
    mobileInput,
    selectedCountry,
    email,
    heightCm,
    heightUnit,
    weightKg,
    weightUnit,
    targetWeightKg,
    onNext,
  ]);

  return (
    <AssessmentStepShell
      step={STEP_NUMBER}
      totalSteps={TOTAL_STEPS}
      title="Tell us about yourself"
      description="This helps us calculate your BMI/BMR and personalize your plan."
      onPrevious={handlePrevious}
      onNext={handleNext}
    >
      <FormGrid>
        <SectionLabel>About you</SectionLabel>

        {/* ── Name (optional) ── */}
        <FieldGroup $fullWidth>
          <Label htmlFor="bi-name">
            Name <span className="optional">(optional)</span>
          </Label>
          <Input
            id="bi-name"
            type="text"
            placeholder="e.g. Sarah"
            value={name}
            maxLength={50}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => markTouched("name")}
            $error={shouldShow("name", !!nameError)}
            aria-invalid={shouldShow("name", !!nameError)}
          />
          {shouldShow("name", !!nameError) && <InlineError>{nameError}</InlineError>}
        </FieldGroup>

        {/* ── Age ── */}
        <FieldGroup>
          <Label htmlFor="bi-age">Age</Label>
          <Input
            id="bi-age"
            type="number"
            inputMode="numeric"
            placeholder="e.g. 29"
            value={ageStr}
            onChange={(e) => setAgeStr(e.target.value)}
            onBlur={() => markTouched("age")}
            $error={shouldShow("age", !!ageResult.error) || (attemptedNext && isBlocked)}
            aria-invalid={shouldShow("age", !!ageResult.error)}
          />
          {shouldShow("age", !!ageResult.error) && <InlineError>{ageResult.error}</InlineError>}
        </FieldGroup>

        {/* ── Gender ── */}
        <FieldGroup>
          <Label>Gender</Label>
          <PillRow role="radiogroup" aria-label="Select gender">
            {GENDER_OPTIONS.map((opt) => (
              <Pill
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={gender === opt.id}
                $active={gender === opt.id}
                onClick={() => handleGenderSelect(opt.id)}
              >
                {opt.label}
              </Pill>
            ))}
          </PillRow>
          {shouldShow("gender", !!genderError) && <InlineError>{genderError}</InlineError>}
        </FieldGroup>

        <SectionLabel>Contact details</SectionLabel>

        {/* ── Mobile number + country code ── */}
        <FieldGroup $fullWidth>
          <Label htmlFor="bi-mobile-country">
            Mobile number
            {!MOBILE_REQUIRED && <span className="optional">(optional)</span>}
          </Label>
          <PhoneRow>
            <CountryCodeSelect
              id="bi-mobile-country"
              countries={COUNTRY_CODES}
              value={selectedCountry.code}
              onChange={handleMobileCountryChange}
              error={shouldShow("mobile", !!mobileError)}
              ariaLabel="Country code"
            />
            <Input
              id="bi-mobile"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              placeholder={selectedCountry.code === "IN" ? "e.g. 9876543210" : "Mobile number"}
              value={mobileInput}
              onChange={handleMobileChange}
              onBlur={() => markTouched("mobile")}
              $error={shouldShow("mobile", !!mobileError)}
              aria-invalid={shouldShow("mobile", !!mobileError)}
              aria-describedby="bi-mobile-help"
            />
          </PhoneRow>
          {shouldShow("mobile", !!mobileError) && <InlineError>{mobileError}</InlineError>}
          <HelperText id="bi-mobile-help">
            We'll use this to send your plan and important updates. No
            promotional messages.
          </HelperText>
        </FieldGroup>

        {/* ── Email (optional) ── */}
        <FieldGroup $fullWidth>
          <Label htmlFor="bi-email">
            Email <span className="optional">(optional)</span>
          </Label>
          <Input
            id="bi-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="e.g. sarah@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => markTouched("email")}
            $error={shouldShow("email", !!emailError)}
            aria-invalid={shouldShow("email", !!emailError)}
          />
          {shouldShow("email", !!emailError) && <InlineError>{emailError}</InlineError>}
        </FieldGroup>

        {/* ── Age < 13 blocking notice ── */}
        {ageResult.blocked && (
          <BlockingNotice role="alert">
            <strong>We can't continue with this age just yet.</strong> Our
            plans are designed for adults. Please consult a pediatric
            health professional for nutrition guidance suited to younger
            ages.
          </BlockingNotice>
        )}

        {/* ── Age 13-17 blocking notice (DEC-08: planner is adults 18+) ── */}
        {ageResult.minor && (
          <BlockingNotice role="alert">
            <strong>Our personalized diet planner is currently for ages
            18 and up.</strong> Calorie and macro targets are calculated
            with an adult-specific formula that isn't appropriate for
            teens, so we can't generate a personalized plan here. Please
            speak with a pediatrician, school nutritionist, or family
            doctor for guidance suited to your age.
          </BlockingNotice>
        )}

        <SectionLabel>Body measurements</SectionLabel>

        {/* ── Height ── */}
        <FieldGroup>
          <FieldHeaderRow>
            <Label htmlFor="bi-height-cm">Height</Label>
            <UnitToggle role="group" aria-label="Height unit">
              <UnitButton
                type="button"
                $active={heightUnit === "cm"}
                onClick={() => handleHeightUnitToggle("cm")}
              >
                cm
              </UnitButton>
              <UnitButton
                type="button"
                $active={heightUnit === "ft"}
                onClick={() => handleHeightUnitToggle("ft")}
              >
                ft/in
              </UnitButton>
            </UnitToggle>
          </FieldHeaderRow>

          {heightUnit === "cm" ? (
            <SplitInputWrap>
              <Input
                id="bi-height-cm"
                type="number"
                inputMode="decimal"
                placeholder="e.g. 165"
                value={heightCmInput}
                onChange={(e) => setHeightCmInput(e.target.value)}
                onBlur={() => markTouched("height")}
                $error={shouldShow("height", !!heightError)}
                aria-invalid={shouldShow("height", !!heightError)}
              />
              <span className="unit-suffix">cm</span>
            </SplitInputWrap>
          ) : (
            <SplitInputRow>
              <SplitInputWrap>
                <Input
                  type="number"
                  inputMode="numeric"
                  placeholder="ft"
                  value={heightFeetInput}
                  onChange={(e) => setHeightFeetInput(e.target.value)}
                  onBlur={() => markTouched("height")}
                  $error={shouldShow("height", !!heightError)}
                  aria-label="Height — feet"
                />
                <span className="unit-suffix">ft</span>
              </SplitInputWrap>
              <SplitInputWrap>
                <Input
                  type="number"
                  inputMode="numeric"
                  placeholder="in"
                  value={heightInchesInput}
                  onChange={(e) => setHeightInchesInput(e.target.value)}
                  onBlur={() => markTouched("height")}
                  $error={shouldShow("height", !!heightError)}
                  aria-label="Height — inches"
                />
                <span className="unit-suffix">in</span>
              </SplitInputWrap>
            </SplitInputRow>
          )}
          {shouldShow("height", !!heightError) && <InlineError>{heightError}</InlineError>}
        </FieldGroup>

        {/* ── Weight ── */}
        <FieldGroup>
          <FieldHeaderRow>
            <Label htmlFor="bi-weight">Weight</Label>
            <UnitToggle role="group" aria-label="Weight unit">
              <UnitButton
                type="button"
                $active={weightUnit === "kg"}
                onClick={() => handleWeightUnitToggle("kg")}
              >
                kg
              </UnitButton>
              <UnitButton
                type="button"
                $active={weightUnit === "lb"}
                onClick={() => handleWeightUnitToggle("lb")}
              >
                lb
              </UnitButton>
            </UnitToggle>
          </FieldHeaderRow>
          <SplitInputWrap>
            <Input
              id="bi-weight"
              type="number"
              inputMode="decimal"
              placeholder={weightUnit === "kg" ? "e.g. 62" : "e.g. 137"}
              value={weightUnit === "kg" ? weightKgInput : weightLbInput}
              onChange={(e) =>
                weightUnit === "kg"
                  ? setWeightKgInput(e.target.value)
                  : setWeightLbInput(e.target.value)
              }
              onBlur={() => markTouched("weight")}
              $error={shouldShow("weight", !!weightError)}
              aria-invalid={shouldShow("weight", !!weightError)}
            />
            <span className="unit-suffix">{weightUnit}</span>
          </SplitInputWrap>
          {shouldShow("weight", !!weightError) && <InlineError>{weightError}</InlineError>}
        </FieldGroup>

        {/* ── Target Weight (optional) ── */}
        <FieldGroup $fullWidth>
          <Label htmlFor="bi-target-weight">
            Target Weight <span className="optional">(optional)</span>
          </Label>
          <SplitInputWrap style={{ maxWidth: 220 }}>
            <Input
              id="bi-target-weight"
              type="number"
              inputMode="decimal"
              placeholder={weightUnit === "kg" ? "e.g. 58" : "e.g. 128"}
              value={targetWeightInput}
              onChange={(e) => setTargetWeightInput(e.target.value)}
              onBlur={() => markTouched("targetWeight")}
              $error={shouldShow("targetWeight", !!targetWeightError)}
              aria-invalid={shouldShow("targetWeight", !!targetWeightError)}
            />
            <span className="unit-suffix">{weightUnit}</span>
          </SplitInputWrap>
          {shouldShow("targetWeight", !!targetWeightError) && (
            <InlineError>{targetWeightError}</InlineError>
          )}
          <HelperText>
            If your goal turns out to be weight loss, we'll double-check
            this against your current weight on the next screen.
          </HelperText>
        </FieldGroup>
      </FormGrid>
    </AssessmentStepShell>
  );
};

BasicInfoStep.displayName = "BasicInfoStep";
export default React.memo(BasicInfoStep);